import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { LinearProgress, Paper, Tooltip } from "@mui/material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useUrlPaging from "../../hooks/useUrlPaging";
import AdsAPIs from "../../apiRoutes/Ads";
import {
  GET_AD_LAUNCHES_QUERY_KEY,
  GET_META_SYNC_RUNS_QUERY_KEY,
} from "../../utils/constants/queryKeys";
import AD_LAUNCH_EXPORT_COLUMNS from "../../utils/constants/adLaunchExportColumns";
import SectionHeader from "../common/SectionHeader";
import CustomModal from "../common/CustomModal";
import { formatDisplayDateTime } from "../../utils/helperFunctions";
import { Button } from "../ui/button";
import AdLaunchDetailModal from "./Modals/AdLaunchDetailModal";
import LaunchesTable from "./LaunchesTable";
import LaunchWizardModal from "./LaunchWizardModal";
import LaunchFilterBar from "./LaunchFilterBar";
import LaunchExportMenu from "./LaunchExportMenu";
import SyncLiveStatus from "./SyncLiveStatus";
import { Rocket, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import {
  fetchAllFilteredRows,
  downloadExcelFromRows,
  exportFilename,
} from "../../utils/exportFilteredListToExcel";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSION_CODES } from "../../utils/constants/permissions";
import {
  DUE_TODAY_STATUS,
  LAUNCH_STATUS_HINTS,
} from "../../utils/constants/AdLaunch";

const AdLaunchSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get("status") || ""
  );
  const [filters, setFilters] = useState(null);
  const { page, setPage, pageSize, setPageSize } = useUrlPaging();
  const [launchModalOpen, setLaunchModalOpen] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [cancelConfirmRow, setCancelConfirmRow] = useState(null);
  const [markLiveConfirmRow, setMarkLiveConfirmRow] = useState(null);
  // Detail modal
  const [detailModalLaunchId, setDetailModalLaunchId] = useState(null);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canViewLaunch = hasPermission(PERMISSION_CODES.AD_LAUNCH_VIEW);
  const canCreateLaunch = hasPermission(PERMISSION_CODES.AD_LAUNCH_CREATE);
  const canSetLive = hasPermission(PERMISSION_CODES.AD_LAUNCH_SET_LIVE);

  const handleFiltersChange = useCallback(
    (next) => setFilters((prev) => (prev?.key === next.key ? prev : next)),
    []
  );

  const buildListParams = (limit, offset) => ({
    axiosPrivate,
    // Only this screen narrows; the dashboard journey table needs the rest.
    launch_only: true,
    status: statusFilter || undefined,
    ...filters?.apiParams,
    limit,
    offset,
  });

  const {
    data: result,
    isLoading,
    isPlaceholderData,
  } = useQuery({
    queryKey: [
      ...GET_AD_LAUNCHES_QUERY_KEY,
      statusFilter,
      filters?.key,
      page,
      pageSize,
    ],
    queryFn: () =>
      AdsAPIs.listLaunches({
        ...buildListParams(pageSize, page * pageSize),
        group_by: "adset",
      }),
    placeholderData: keepPreviousData,
    enabled: !!filters,
  });
  const rows = useMemo(() => result?.data ?? [], [result]);
  const totalCount = result?.count ?? 0;
  const hookCount = result?.hookCount ?? totalCount;

  // readyOnly pins the Due today tab regardless of the pill, so the launch sheet
  // only carries Ready ads whose launch date has arrived.
  const handleExportExcel = async (readyOnly = false) => {
    if (exporting) return;
    setExporting(readyOnly ? "ready" : "view");
    toast.loading("Preparing export…", { id: "export-excel-launch" });
    try {
      const { rows: allRows, total } = await fetchAllFilteredRows({
        fetchPage: ({ limit, offset }) =>
          AdsAPIs.listLaunches({
            ...buildListParams(limit, offset),
            ...(readyOnly ? { status: DUE_TODAY_STATUS } : {}),
          }),
      });
      if (!allRows.length) {
        toast.dismiss("export-excel-launch");
        toast.error("No rows to export.");
        return;
      }
      await downloadExcelFromRows({
        filename: exportFilename(readyOnly ? "ad-launch-ready" : "ad-launch"),
        sheetName: "Ad launch",
        columns: AD_LAUNCH_EXPORT_COLUMNS,
        rows: allRows,
      });
      toast.dismiss("export-excel-launch");
      toast.success(`Exported ${total} row(s).`);
    } catch (e) {
      toast.dismiss("export-excel-launch");
      if (e?.message === "EXPORT_TOO_LARGE") {
        toast.error(
          `Too many rows (${e.total}). Max export is ${e.maxRows}. Narrow filters.`
        );
      } else {
        toast.error(e?.message || "Export failed.");
      }
    } finally {
      setExporting(null);
    }
  };

  // Back to page 1 only when a filter value really changes; a mount-time effect would wipe the page from the URL.
  const filterKey = `${statusFilter}|${filters?.key}`;
  const lastFilterKey = useRef(null);
  useEffect(() => {
    if (!filters) return;
    if (lastFilterKey.current && lastFilterKey.current !== filterKey)
      setPage(0);
    lastFilterKey.current = filterKey;
  }, [filters, filterKey, setPage]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (statusFilter) next.set("status", statusFilter);
        else next.delete("status");
        return next;
      },
      { replace: true }
    );
  }, [statusFilter, setSearchParams]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: GET_AD_LAUNCHES_QUERY_KEY });

  const retryMutation = useMutation({
    mutationFn: (id) => AdsAPIs.retryLaunch({ axiosPrivate, id }),
    onSuccess: () => {
      invalidate();
      toast.success("Retry queued.");
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Could not retry this launch.");
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (launchId) =>
      AdsAPIs.cancelLaunch({ axiosPrivate, id: launchId }),
    onSuccess: () => {
      invalidate();
      toast.success("Launch cancelled.");
      setCancelConfirmRow(null);
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Failed to cancel launch.");
    },
  });

  const setLiveMutation = useMutation({
    mutationFn: (adModerateId) =>
      AdsAPIs.markLaunchLive({ axiosPrivate, ad_moderate_id: adModerateId }),
    onSuccess: () => {
      invalidate();
      toast.success("Marked as Live.");
      setMarkLiveConfirmRow(null);
    },
  });

  // The sweep runs on Celery; its saved run is polled while it works, and the table refreshes when it ends.
  const { data: syncRuns } = useQuery({
    queryKey: GET_META_SYNC_RUNS_QUERY_KEY,
    queryFn: () => AdsAPIs.listSyncLiveRuns({ axiosPrivate, limit: 1 }),
    enabled: canViewLaunch,
    refetchInterval: (query) =>
      query.state.data?.[0]?.status === "running" ? 5000 : false,
  });
  const latestSync = syncRuns?.[0];
  const syncRunning = latestSync?.status === "running";
  const prevSyncStatus = useRef(latestSync?.status);
  useEffect(() => {
    if (
      prevSyncStatus.current === "running" &&
      latestSync &&
      latestSync.status !== "running"
    ) {
      invalidate();
      if (latestSync.status === "success")
        toast.success(`Sync finished: ${latestSync.marked_live} marked live.`);
      else toast.error("Sync did not finish. Open the report for details.");
    }
    prevSyncStatus.current = latestSync?.status;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestSync?.id, latestSync?.status]);

  const syncLiveMutation = useMutation({
    mutationFn: () => AdsAPIs.syncLaunchLive({ axiosPrivate }),
    onSuccess: (data) => {
      if (data?.already_running) toast("A sync is already running.");
      else
        toast.success("Sync started. The report shows here when it finishes.");
      if (data?.run)
        queryClient.setQueryData(GET_META_SYNC_RUNS_QUERY_KEY, [data.run]);
    },
  });

  // Totals across the whole filtered set — `rows` is only the current page.
  const statusCounts = result?.statusCounts ?? {};

  // One tab bar replaces the old count pills and the status dropdown; "" is All.
  const statusTabs = [
    { key: "ready", label: "Ready" },
    { key: DUE_TODAY_STATUS, label: "Due today" },
    { key: "scheduled", label: "Scheduled" },
    { key: "queued", label: "Queued" },
    { key: "processing", label: "Processing" },
    { key: "meta_scheduled", label: "Launch scheduled" },
    { key: "success", label: "Live" },
    { key: "schedule_failed", label: "Schedule failed", alert: true },
    { key: "failed", label: "Launch failed", alert: true },
    { key: "cancelled", label: "Cancelled" },
  ].map((t) => ({ ...t, count: statusCounts[t.key] ?? 0 }));
  // Due today is a subset of Ready, so it stays out of the All total.
  const allCount = statusTabs
    .filter((t) => t.key !== DUE_TODAY_STATUS)
    .reduce((sum, t) => sum + t.count, 0);

  return (
    <section className="p-4 md:p-6">
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <SectionHeader title="Ad Launch" />
          <p className="mt-1 text-sm text-gray-500">
            Launch final-approved hooks to Meta and track them once they are
            live.
          </p>
        </div>
        <div className="flex min-w-0 flex-col gap-2 md:items-end">
          <div className="flex flex-wrap items-center gap-2 md:justify-end">
            {canSetLive && (
              <Button
                variant="outline"
                onClick={() => syncLiveMutation.mutate()}
                disabled={syncLiveMutation.isPending || syncRunning}
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${syncLiveMutation.isPending || syncRunning ? "animate-spin" : ""}`}
                />
                {syncLiveMutation.isPending || syncRunning
                  ? "Syncing…"
                  : "Sync live with Meta"}
              </Button>
            )}
            {canCreateLaunch && (
              <Button
                onClick={() => setLaunchModalOpen(true)}
                className="bg-[#372e5e] hover:bg-[#2a2349] text-white"
              >
                <Rocket className="w-4 h-4 mr-2" />
                Launch
              </Button>
            )}
          </div>
          <SyncLiveStatus run={latestSync} />
        </div>
      </div>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <div
          className="flex gap-1 overflow-x-auto border-b border-gray-200 px-3"
          id="HideScroll"
        >
          {[{ key: "", label: "All", count: allCount }, ...statusTabs].map(
            (t) => {
              const active = statusFilter === t.key;
              return (
                <Tooltip
                  key={t.key || "all"}
                  arrow
                  enterDelay={300}
                  placement="top"
                  title={LAUNCH_STATUS_HINTS[t.key || "all"] || ""}
                >
                  <button
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      setStatusFilter(t.key);
                      setPage(0);
                    }}
                    className={`-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                      active
                        ? "border-primaryPurple text-gray-900"
                        : "border-transparent text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    {t.label}
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none ${
                        t.alert && t.count
                          ? "bg-rose-100 text-rose-700"
                          : active
                            ? "bg-[#efeefb] text-primaryPurple"
                            : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {t.count}
                    </span>
                  </button>
                </Tooltip>
              );
            }
          )}
        </div>
        <div className="relative h-0">
          {isPlaceholderData && (
            <LinearProgress
              sx={{
                position: "absolute",
                top: "-2px",
                left: 0,
                right: 0,
                height: 2,
                bgcolor: "#efedfc",
                "& .MuiLinearProgress-bar": { bgcolor: "#7f7ad5" },
              }}
            />
          )}
        </div>
        <div className="space-y-3 border-b border-gray-200 p-4">
          <LaunchFilterBar
            statusActive={!!statusFilter}
            onClearStatus={() => setStatusFilter("")}
            onChange={handleFiltersChange}
            actions={
              canViewLaunch && (
                <LaunchExportMenu
                  exporting={exporting}
                  disabled={isLoading || !filters}
                  hookCount={hookCount}
                  onExport={handleExportExcel}
                />
              )
            }
          />
        </div>

        <LaunchesTable
          rows={rows}
          isLoading={isLoading || !filters}
          totalCount={totalCount}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          onPageSizeChange={(n) => {
            setPageSize(n);
            setPage(0);
          }}
          canCreateLaunch={canCreateLaunch}
          canSetLive={canSetLive}
          onOpenDetail={setDetailModalLaunchId}
          onOpenRequest={(id) => navigate(`/dashboard/ad-request/${id}`)}
          onRetry={(id) => retryMutation.mutate(id)}
          retryPending={retryMutation.isPending}
          onCancelRow={setCancelConfirmRow}
          cancelPending={cancelMutation.isPending}
          onMarkLiveRow={setMarkLiveConfirmRow}
          setLivePending={setLiveMutation.isPending}
        />
      </Paper>

      <LaunchWizardModal
        open={launchModalOpen}
        onClose={() => setLaunchModalOpen(false)}
      />

      <CustomModal
        open={!!cancelConfirmRow}
        onClose={() => setCancelConfirmRow(null)}
        title="Cancel scheduled launch?"
        isButtonsRequired={false}
        className="md:w-[50%] lg:w-[40%]"
      >
        {cancelConfirmRow && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-700">
              This will cancel the scheduled launch for
              <span className="font-medium">
                {" "}
                {cancelConfirmRow.ad_name ||
                  `Moderation #${cancelConfirmRow.ad_moderate_id}`}
              </span>
              . The launch will <span className="font-medium">not</span> be
              dispatched at
              <span className="font-medium">
                {" "}
                {cancelConfirmRow.scheduled_at
                  ? formatDisplayDateTime(cancelConfirmRow.scheduled_at)
                  : "the scheduled time"}
              </span>
              . You can re-launch the same ad later from the normal launch flow.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <Button
                variant="outline"
                onClick={() => setCancelConfirmRow(null)}
                disabled={cancelMutation.isPending}
              >
                Keep schedule
              </Button>
              <Button
                variant="destructive"
                onClick={() =>
                  cancelMutation.mutate(cancelConfirmRow.launch_id)
                }
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? "Cancelling…" : "Cancel launch"}
              </Button>
            </div>
          </div>
        )}
      </CustomModal>

      <CustomModal
        open={!!markLiveConfirmRow}
        onClose={() => setMarkLiveConfirmRow(null)}
        title="Mark as Live?"
        isButtonsRequired={false}
        className="md:w-[50%] lg:w-[40%]"
      >
        {markLiveConfirmRow && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-700">
              This does <span className="font-medium">not</span> launch the ad
              on Meta. It only marks
              <span className="font-medium">
                {" "}
                {markLiveConfirmRow.ad_name ||
                  `Moderation #${markLiveConfirmRow.ad_moderate_id}`}
              </span>{" "}
              as Live for ads that were already launched{" "}
              <span className="font-medium">outside the platform</span>. Use
              this only when the ad is actually running on Meta.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <Button
                variant="outline"
                onClick={() => setMarkLiveConfirmRow(null)}
                disabled={setLiveMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() =>
                  setLiveMutation.mutate(markLiveConfirmRow.ad_moderate_id)
                }
                disabled={setLiveMutation.isPending}
              >
                {setLiveMutation.isPending ? "Marking…" : "Mark Live"}
              </Button>
            </div>
          </div>
        )}
      </CustomModal>

      <AdLaunchDetailModal
        open={!!detailModalLaunchId}
        onClose={() => setDetailModalLaunchId(null)}
        launchId={detailModalLaunchId}
        canRetry={canCreateLaunch}
        onRetry={(id) => retryMutation.mutate(id)}
      />
    </section>
  );
};

export default AdLaunchSection;
